import { Component } from "@angular/core";
import { User } from "../../models/user";
import { UserService } from "../../services/user.service";
import { UserProfile } from "../../models/user-profile";
import { UserProfileService } from "../../services/user-profile.service";
import { Router } from "@angular/router";
import { UsersActions } from "../../store/actions/users.actions";
import { Store } from "@ngrx/store";
import { UserProfilesActions } from "../../store/actions/userProfiles.actions";
import { ConfirmComponent } from "../../modals/confirm/confirm.component";
import { MdDialog } from "@angular/material";
import { Title } from "@angular/platform-browser";
import { AdminService } from "../../services/admin.service";
import { SuccessComponent } from "../../modals/success/success.component";
import { FeatureCountries } from "../../models/featureCountries";
import { Language } from "../../models/language";
import { ActionService } from "../../services/action.service";
import { LanguageService } from "../../services/language.service";
import { SidenavService } from "../../services/sidenav.service";
import * as _ from "lodash";
import { Market } from "../../models/market";
import { DataDuplicateLanguage } from "../../models/dataDuplicateLanguage";

@Component({
  selector: "app-admin",
  templateUrl: "./admin.component.html",
  styleUrls: ["./admin.component.scss"],
})
export class AdminComponent {
  users: User[];
  profiles: UserProfile[];
  userSearch: string;
  profileSearch: string;
  editMode: boolean;

  // features values
  features: FeatureCountries[];
  initialFeatures: FeatureCountries[];

  dataDuplicateLanguage: DataDuplicateLanguage;
  dataDuplicateLanguageEditionMode: boolean;

  // Boolean enable feature edition
  featureEditionMode: boolean;

  // Boolean
  // Languages for Features edition
  languages: Language[];
  allLanguage: Language;

  // Markets
  markets: Market[];

  /**
   * Initialize variables and load data
   * @param {ActionService} actionService
   * @param {LanguageService} languageService
   * @param {UserService} userService
   * @param {UserProfileService} userProfileService
   * @param {UsersActions} usersActions
   * @param {UserProfilesActions} userProfilesActions
   * @param {AdminService} adminService
   * @param {Store<any>} store
   * @param {MdDialog} mdDialog
   * @param {Router} router
   * @param {Title} title
   */

  constructor(
    private actionService: ActionService,
    private languageService: LanguageService,
    private userService: UserService,
    private userProfileService: UserProfileService,
    private usersActions: UsersActions,
    private userProfilesActions: UserProfilesActions,
    private adminService: AdminService,
    private store: Store<any>,
    private mdDialog: MdDialog,
    private router: Router,
    private title: Title,
    private sidenavService: SidenavService
  ) {
    // Set the page title
    this.title.setTitle("Taiga - Admin");
    this.sidenavService.currentTab = "Admin";

    this.dataDuplicateLanguage = new DataDuplicateLanguage([]);

    // Fetch all users from the API
    this.userService
      .getAllUsers()
      .subscribe((users) => this.usersActions.setUsers(users));

    // Fetch all the user profiles from the API
    this.userProfileService
      .getAllUserProfiles()
      .subscribe((profiles) =>
        this.userProfilesActions.setUserProfiles(profiles)
      );

    // Get all users from the store
    this.store.select("usersState").subscribe((users) => {
      this.users = users;
    });

    // Get all user profiles from the store
    this.store
      .select("userProfilesState")
      .subscribe((profiles) => (this.profiles = profiles));

    this.loadMarkets();

    // Fetch all the feature by country from the API
    this.actionService
      .getAllFeatureLanguages()
      .subscribe((featureLanguages) => {
        this.features = featureLanguages;
        this.initialFeatures = _.cloneDeep(this.features);
      });

    // Check user right for this component
    this.adminService.init().subscribe(() => {
      this.editMode = this.adminService.can("Admin Panel", "Edit");
    });

    // Feature edition set to false by default
    this.featureEditionMode = false;

    // Get all languages
    this.languageService.getAllLanguages().subscribe((languages) => {
      this.languages = languages;
      this.allLanguage = new Language({
        id: -1,
        isDefault: false,
        value: "all",
      });

      this.actionService
        .getDataDuplicateLanguage()
        .subscribe((dataDuplicateLanguage) => {
          if (dataDuplicateLanguage.length != 0) {
            var listLanguagesToDuplicate = dataDuplicateLanguage.map(
              (x) => x.broadcastCountry
            );
            var languageId = dataDuplicateLanguage[0].languageId;
            this.dataDuplicateLanguage.languages = this.languages.filter((x) =>
              listLanguagesToDuplicate.includes(x.value)
            );
            this.dataDuplicateLanguage.languageToDuplicate =
              this.languages.find((x) => x.id === languageId);
          }
          this.dataDuplicateLanguageEditionMode = false;
        });
    });
  }

  loadMarkets() {
    this.markets = [];
    this.userService
      .getAllMarkets()
      .subscribe((markets) => (this.markets = markets));
  }
  // Open new user modal
  addUser() {
    this.router.navigate([{ outlets: { modal: ["new", "user"] } }]);
  }

  /**
   * Open edit user modal
   * @param {User} user
   */
  editUser(user: User) {
    this.router.navigate([{ outlets: { modal: ["edit", "user", user.id] } }]);
  }

  // Open new user profile modal
  addProfile() {
    this.router.navigate([{ outlets: { modal: ["new", "user-profile"] } }]);
  }

  /**
   * Open edit user profile modal
   * @param {UserProfile} userProfile
   */
  editProfile(userProfile: UserProfile) {
    this.router.navigate([
      { outlets: { modal: ["edit", "user-profile", userProfile.id] } },
    ]);
  }

  /**
   * Delete user profile with an API call
   * @param {UserProfile} profile
   */
  deleteProfile(profile: UserProfile) {
    const data = {
      title: "Do you want to delete this profile ?",
      message: "Are you sure to delete this profile ?",
    };
    this.mdDialog
      .open(ConfirmComponent, { data: data })
      .afterClosed()
      .subscribe((res) => {
        if (res === true) {
          this.userProfileService
            .deleteProfile(profile)
            .subscribe((profiles) => {
              this.userProfilesActions.setUserProfiles(profiles);
              this.mdDialog.closeAll();
              this.mdDialog.open(SuccessComponent, {
                data: { message: "Profile has been deleted" },
              });
            });
        }
      });
  }

  /**
   * Delete user with an API call
   * @param {User} user
   */
  deleteUser(user: User) {
    const title = "Do you want to delete this user ?";
    const message = "Are you sure to delete this user ?";
    this.mdDialog
      .open(ConfirmComponent, { data: { title: title, message: message } })
      .afterClosed()
      .subscribe((res) => {
        if (res === true) {
          this.userService.deleteUser(user).subscribe((users) => {
            this.usersActions.setUsers(users);
            this.mdDialog.closeAll();
            this.mdDialog.open(SuccessComponent, {
              data: { message: "User has been deleted" },
            });
          });
        }
      });
  }

  /**
   * Unlock user with an API call
   * @param {User} user
   */
  unlockUser(user: User) {
    const title = "Do you want to unlock this user ?";
    const message = "Are you sure to unlock this user ?";
    this.mdDialog
      .open(ConfirmComponent, { data: { title: title, message: message } })
      .afterClosed()
      .subscribe((res) => {
        if (res === true) {
          this.userService.unlockUser(user).subscribe((users) => {
            this.usersActions.setUsers(users);
            this.mdDialog.closeAll();
            this.mdDialog.open(SuccessComponent, {
              data: { message: "User has been unlocked." },
            });
          });
        }
      });
  }

  // enable edit feature mode
  editFeatures() {
    this.featureEditionMode = true;
  }

  // disable edit feature mode and save
  saveFeatures() {
    // init list of feature/language to changes
    let featureLanguagesToChange = [];

    // for feature in table
    this.features.forEach((feature) => {
      // for the feature, get all initial languages enabled
      let initialLanguages = this.initialFeatures.find(
        (inf) => inf.code === feature.code
      ).languages;
      // Get all removed languages
      let removed = initialLanguages
        .filter(this.compare(feature.languages))
        .map(function (removedLanguage) {
          return { languageId: removedLanguage.id, featureId: feature.id };
        });
      // Get all added languages
      let added = feature.languages
        .filter(this.compare(initialLanguages))
        .map(function (addedLanguage) {
          return { languageId: addedLanguage.id, featureId: feature.id };
        });
      // add them to list of feature/language to changes
      featureLanguagesToChange = featureLanguagesToChange
        .concat(removed)
        .concat(added);
    });
    featureLanguagesToChange.forEach((featureLanguageToChange) => {
      if (featureLanguageToChange.languageId !== -1) {
        this.actionService
          .changeFeatureAvailability(
            featureLanguageToChange.languageId,
            featureLanguageToChange.featureId
          )
          .subscribe();
      }
    });

    // initialfeatures has to be reset
    this.initialFeatures = _.cloneDeep(this.features);
    this.featureEditionMode = false;
  }

  changeFeatureLanguages(code: string) {
    let feature = this.features.find((f) => f.code === code);
    if (!feature.allLanguage && feature.languages.includes(this.allLanguage)) {
      feature.languages = Array.from(
        new Set(feature.languages.concat(this.languages))
      );
      feature.allLanguage = true;
    } else if (
      feature.allLanguage &&
      !feature.languages.includes(this.allLanguage)
    ) {
      feature.allLanguage = false;
      feature.languages = [];
    }
  }

  // enable edit dataDuplicateLanguage mode
  editDataDuplicateLanguage() {
    this.dataDuplicateLanguageEditionMode = true;
  }

  saveDataDuplicateLanguage() {
    this.dataDuplicateLanguageEditionMode = false;
    this.actionService
      .saveDataDuplicateLanguage(this.dataDuplicateLanguage)
      .subscribe();
  }

  clickOnSelectAllDuplicateLanguage() {
    if (this.dataDuplicateLanguage.languages.length == this.languages.length) {
      this.dataDuplicateLanguage.languages = [];
    } else {
      this.dataDuplicateLanguage.languages = Array.from(
        new Set(this.dataDuplicateLanguage.languages.concat(this.languages))
      );
    }
  }

  compare(otherArray) {
    return function (current) {
      return (
        otherArray.filter(function (other) {
          return (
            other.value == current.value && other.display == current.display
          );
        }).length == 0
      );
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/admin/admin.component.ts